<script setup lang="ts">
import { getFormattedYearRange } from '~/composables/get-formatted-date-range'

const { t } = useI18n()

const showArchive = useState('show-people-archive', () => false)

// the archive fetches on mount; until it reports back there is nothing to show,
// so the toggle stays a plain span rather than a button that does nothing
const archiveLoaded = ref(false)
const canShowArchive = computed(() => archiveLoaded.value && !showArchive.value)

interface GuestPerson {
  id: string
  firstName: string
  lastName: string
  isExternal: boolean
  person?: { id: string; slug: string; title: string; sortValue?: string }
}

interface GuestDoc {
  id: string
  title: string
  dateStart: string
  dateEnd: string
  people: GuestPerson[]
}

const props = defineProps<{
  data: PeoplePage,
  entries: PersonOccurrence[],
  guests: GuestDoc[],
}>()

// grouped on the rendered label, not on the raw dates: occurrences that read
// as the same period (e.g. a whole year) are often stored with different times
// of day, which would otherwise split them into separate columns.
//
// Ongoing roles are the exception: each one starts in a different year, so
// their own labels ("2018–heden", "2021–heden") would give every permanent
// tutor a column of their own. They belong together as one "current" group,
// listed first -- the individual start years live on the person's page.
const ONGOING_KEY = '__ongoing__'

const groupByRange = (entries: PersonOccurrence[]) => {
  const groups: Record<string, { label: string; startTime: number; occurrences: PersonOccurrence[] }> = {}
  entries.forEach((occurrence) => {
    const key = occurrence.isOngoing
      ? ONGOING_KEY
      : getFormattedYearRange(occurrence.dateStart, occurrence.dateEnd)
    if (!groups[key]) {
      groups[key] = { label: key === ONGOING_KEY ? t('labels.current') : key, startTime: 0, occurrences: [] }
    }
    // the label can cover occurrences stored with slightly different dates, so the
    // group sorts on the latest start it contains -- same rule as the archive
    const time = new Date(occurrence.dateStart).getTime()
    if (Number.isFinite(time) && time > groups[key].startTime) groups[key].startTime = time
    groups[key].occurrences.push(occurrence)
  })
  const ordered = Object.entries(groups)
    .sort(([a, groupA], [b, groupB]) => {
      if (a === ONGOING_KEY) return -1
      if (b === ONGOING_KEY) return 1
      return groupB.startTime - groupA.startTime
    })
    .map(([, group]) => group)
  return ordered.map(group => ({
    ...group,
    occurrences: sortBySortValue(group.occurrences, o => o.person?.sortValue ?? o.title),
  }))
}

// section order is fixed by the client: participants, staff, tutors -- then
// the past tutors and guests below. Within a section the groups run newest first.
const sections = computed(() => [
  { key: 'participant', groups: groupByRange(props.entries.filter((e: PersonOccurrence) => e.type === 'participant')) },
  { key: 'staff', groups: groupByRange(props.entries.filter((e: PersonOccurrence) => e.type === 'staff')) },
  { key: 'tutor', groups: groupByRange(props.entries.filter((e: PersonOccurrence) => e.type === 'tutor')) },
].filter(s => s.groups.length > 0))

const hasGuests = computed(() => props.guests.length > 0)

// the past tutors follow the current ones: a single group without a period,
// made up of the same kind of rows as a guest period and sorted the same way.
// The query always populates the relationship, which the generated type leaves open.
const pastTutors = computed(() => sortBySortValue(
  (props.data.pastTutors ?? []).map(p => ({ ...p, person: typeof p.person === 'object' ? p.person : undefined })),
  p => p.person?.sortValue ?? `${p.lastName ?? ''} ${p.firstName ?? ''}`.trim(),
))
const hasPastTutors = computed(() => pastTutors.value.length > 0)

const getOccurrenceCountForGroup = (group: { occurrences: PersonOccurrence[] }) => {
  const peopleSet = new Set<string>()
  group.occurrences.forEach((occurrence) => {
    if (occurrence.person?.id) {
      peopleSet.add(occurrence.person.id)
    }
  })
  return peopleSet.size
}

const sortedGuestPeople = (guest: GuestDoc) =>
  sortBySortValue(guest.people, p => p.person?.sortValue ?? `${p.lastName} ${p.firstName}`.trim())

const peopleInGuestsCount = computed(() => {
  const peopleSet = new Set<string>()
  props.guests.forEach((guest) => {
    guest.people.forEach((p) => {
      if (p.person?.id) {
        peopleSet.add(p.person.id)
      }
    })
  })
  return peopleSet.size
})
</script>

<template>
  <div 
    v-if="sections.length > 0 || hasGuests || hasPastTutors"
    class="grid grid-cols-3 <md:grid-cols-2 <sm:grid-cols-1 gap-x-2 gap-y-2 mx-1"
  >
    <template v-for="(section, index) in sections" :key="section.key" >
      <div v-for="group in section.groups" :key="group.label">
        <div 
          class="text-md mb-1"
        >
          <span class="block">{{ t(`pages.people.${section.key}`, { count: section.groups.reduce((acc, group) => acc + getOccurrenceCountForGroup(group), 0) }) }}</span> 
          <span class="block">{{ group.label }}</span>
        </div>
        
        <ul>
          <li v-for="occurrence in group.occurrences" :key="occurrence.id">
            <shared-person-link :name="occurrence.title" :slug="occurrence.person?.slug" />
          </li>
        </ul>
      </div>
    </template>

    <div v-if="hasPastTutors">
      <div 
        class="text-md mb-1"
      >
        <span class="block">{{ t('pages.people.tutor', { count: pastTutors.length }) }}</span>
        <span class="block">{{ t('labels.past') }}</span>
      </div>
      <ul>
        <li v-for="p in pastTutors" :key="p.id ?? p.person?.id ?? `${p.firstName} ${p.lastName}`">
          <shared-person-link
            :name="p.person?.title ?? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim()"
            :slug="p.person?.slug"
          />
        </li>
      </ul>
    </div>

    <template v-if="hasGuests" >
      <div v-for="guest in props.guests" :key="guest.id">
        <div 
          class="text-md mb-1"
        >
          <span class="block">{{ t('pages.people.guest', { count: peopleInGuestsCount }) }}</span> 
          <span class="block">{{ getFormattedYearRange(guest.dateStart, guest.dateEnd) }}</span>
        </div>
        <ul>
          <li v-for="p in sortedGuestPeople(guest)" :key="p.id">
            <shared-person-link
              :name="p.person?.title ?? `${p.firstName} ${p.lastName}`.trim()"
              :slug="p.person?.slug"
            />
          </li>
        </ul>
      </div>
    </template>

    <!-- `self-start` keeps this the height of the shape instead of the grid
         row: a full-height <button> has its content vertically centred by the
         UA (a <span> does not), so without it the shape drops half a row the
         moment the archive finishes loading and the toggle becomes clickable. -->
    <component
      v-if="data?.maskShape && !showArchive"
      :is="canShowArchive ? 'button' : 'span'"
      class="group pointer-events-none self-start"
      @click="canShowArchive && (showArchive = true)"
    >
      <core-mask-shape-with-text
        :mask-shape="data.maskShape"
        :text="showArchive ? '' : t(archiveLoaded ? 'pages.people.showArchive' : 'labels.loading')"
        :background-color="showArchive ? 'var(--color--gray)' : null"
        :hover-color="canShowArchive ? 'var(--color--gray)' : null"
      />
    </component>

    <pages-people-archive
      v-show="showArchive"
      class="col-span-full mt-1"
      @loaded="archiveLoaded = true"
    />
  </div>
</template>
